import { ui } from "../app/store.js";

// App icons are from each publisher's official Google Play listing.
const apps = {
  google: {
    name: "Google Authenticator",
    logo: new URL("../assets/authenticators/google.png", import.meta.url).href,
    android:
      "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
    iphone: "https://apps.apple.com/app/google-authenticator/id388497605",
    scan: "In Google Authenticator, tap + and choose Scan a QR code.",
    manual:
      "In Google Authenticator, tap + → Enter a setup key. Name the account Milltech, paste the setup key, select Time based, then add the account.",
  },
  microsoft: {
    name: "Microsoft Authenticator",
    logo: new URL("../assets/authenticators/microsoft.png", import.meta.url)
      .href,
    android:
      "https://play.google.com/store/apps/details?id=com.azure.authenticator",
    iphone: "https://apps.apple.com/app/microsoft-authenticator/id983156458",
    scan: "In Microsoft Authenticator, choose Add account (+) → Other account (Google, Facebook, etc.), then scan the Milltech QR code.",
    manual:
      "In Microsoft Authenticator, choose Add account → Other account → Enter code manually. Name the account Milltech, paste the setup key into Secret key, then select Finish.",
  },
};
const selectedApp = () =>
  Object.hasOwn(apps, ui.authenticatorApp) ? ui.authenticatorApp : null;

export const authenticatorAppInfo = (key) => apps[key] || null;

export function authenticatorAppChoices() {
  const selected = selectedApp();
  const app = selected ? apps[selected] : null;
  return `<div class="auth-app-guide" data-authenticator-choices><p class="auth-app-title">Choose Your Authenticator App</p><p>Choose either app. If you already use one, you can keep using it for Milltech.</p><div class="auth-app-choices" role="group" aria-label="Choose your authenticator app">${Object.entries(
    apps,
  )
    .map(
      ([key, item]) =>
        `<button type="button" class="auth-app-choice" data-authenticator-app="${key}" aria-pressed="${key === selected}"><img src="${item.logo}" width="44" height="44" alt=""><span class="auth-app-choice-name">${item.name}</span><span class="auth-app-choice-state">${key === selected ? "✓ Selected" : "Choose App"}</span></button>`,
    )
    .join(
      "",
    )}</div>${app ? `<div class="auth-app-download"><p class="auth-app-download-title">Get ${app.name}</p><div class="auth-app-links"><a href="${app.android}" target="_blank" rel="noopener noreferrer">Android · Google Play ↗</a><a href="${app.iphone}" target="_blank" rel="noopener noreferrer">iPhone · App Store ↗</a></div><p>${app.scan}</p></div>` : '<p class="small muted">Select the app you will use for Milltech.</p>'}</div>`;
}

export function authenticatorScanStep() {
  return `<li data-authenticator-scan>${apps[selectedApp()]?.scan || "Choose your authenticator app above to see its setup steps."}</li>`;
}

export function authenticatorManualInstructions() {
  return `<p data-authenticator-manual>${apps[selectedApp()]?.manual || "Choose an app above for manual setup steps."}</p>`;
}

export function accountSetupTutorial() {
  return `<button type="button" class="setup-tutorial-trigger" data-setup-tutorial-open aria-haspopup="dialog" aria-controls="account-setup-tutorial">Account Setup Tutorial</button><dialog id="account-setup-tutorial" class="setup-tutorial-modal" aria-labelledby="account-setup-tutorial-title"><div class="setup-tutorial-header"><h2 id="account-setup-tutorial-title">Account Setup Tutorial</h2><button type="button" class="setup-tutorial-close" data-setup-tutorial-close aria-label="Close tutorial">×</button></div><div class="setup-tutorial-content"><p class="setup-tutorial-intro">Have your phone ready. Choose Google Authenticator or Microsoft Authenticator for your Milltech verification codes.</p>${authenticatorAppChoices()}<div class="setup-tutorial-steps"><h3>Set up your account</h3><ol><li><strong>Create your account.</strong> Open the invitation from your administrator, check your work email, then choose and confirm your own password. Owners start with the workspace setup screen.</li><li><strong>Connect your authenticator app.</strong> Follow the setup prompt after creating your account. If you go straight to the workspace, open Settings → Security → Set Up Authenticator. Confirm your Milltech password and follow the instructions for your chosen app to scan the QR code.</li><li><strong>Save your recovery codes.</strong> After adding Milltech in the app, select Next in Milltech. Keep the recovery codes in a password manager or another private place you can reach if you lose your phone. Confirm that you saved them.</li><li><strong>Verify and sign in.</strong> Enter the current six-digit code for Milltech from your chosen app to finish setup. On later sign-ins, use your exact work email, password, and the current app code when asked.</li></ol><p>Using one phone? Choose Enter Setup Key Manually below the QR code.</p>${authenticatorManualInstructions()}</div></div><div class="setup-tutorial-footer"><button type="button" class="pri" data-setup-tutorial-close>Done</button></div></dialog>`;
}

const tutorialRoots = new WeakSet();
const tutorialTriggers = new WeakMap();
export function installAccountSetupTutorial(root = document) {
  if (tutorialRoots.has(root)) return;
  tutorialRoots.add(root);
  root.addEventListener("click", (event) => {
    const trigger = event.target.closest?.("[data-setup-tutorial-open]");
    if (trigger) {
      const dialog = root.querySelector("#account-setup-tutorial");
      if (!dialog) return;
      tutorialTriggers.set(dialog, trigger);
      dialog.showModal();
      return;
    }
    const close = event.target.closest?.("[data-setup-tutorial-close]");
    if (close) {
      close.closest("dialog")?.close();
      return;
    }
    if (event.target.matches?.(".setup-tutorial-modal")) event.target.close();
  });
  root.addEventListener(
    "close",
    (event) => {
      if (!event.target.matches?.(".setup-tutorial-modal")) return;
      const trigger = tutorialTriggers.get(event.target);
      if (trigger?.isConnected) trigger.focus();
    },
    true,
  );
}

const installedRoots = new WeakSet();
export function installAuthenticatorChoices(root = document) {
  if (installedRoots.has(root)) return;
  installedRoots.add(root);
  root.addEventListener("click", (event) => {
    const button = event.target.closest?.("[data-authenticator-app]");
    const key = button?.dataset.authenticatorApp;
    if (
      !Object.hasOwn(apps, key) ||
      ui.authBusy ||
      ui.invitationBusy ||
      ui.settingsBusy
    )
      return;
    ui.authenticatorApp = key;
    // Update only the guide so a password already entered in the form is kept.
    for (const guide of root.querySelectorAll("[data-authenticator-choices]"))
      guide.outerHTML = authenticatorAppChoices();
    for (const step of root.querySelectorAll("[data-authenticator-scan]"))
      step.textContent = apps[key].scan;
    for (const instructions of root.querySelectorAll(
      "[data-authenticator-manual]",
    ))
      instructions.textContent = apps[key].manual;
    root.querySelector(`[data-authenticator-app="${key}"]`)?.focus();
  });
}
